/**
 * ADR-142 §11 — the Contracts tab: the CURRENT-STATE view of a person's
 * contract(s), read-only always.
 *
 * Boundary with the Employment Episodes tab (stated so the two do not
 * drift): Contracts shows the contract(s) in force — terms at a glance —
 * and, for a person with none open, the latest ended contract (§5.5).
 * Episodes shows the FULL history including closed ones, with the
 * lifecycle timeline and the correction/annotation surfaces.
 *
 * Read-only for direct edits (§5.4/§9): terms are set at hire/rehire and
 * changed thereafter by contract amendment; a data error is corrected on
 * the Employment Episodes tab (ADR-019). ADR-113 §7 states the general
 * rule this tab follows: it never writes a FIELD directly, but it hosts
 * the VERBS that emit events against its contract. ADR-143 opened that
 * door with Amend Contract; ADR-113 moves the rest of the
 * contract-addressed lifecycle verbs here — each card carries its
 * contract's own action set (gated on THAT contract's facts, §6), its
 * scheduled-renewal panel with the in-place cancel, and its scheduled-end
 * panel with the end-date verbs. The card ⋮ menu keeps only launcher
 * shortcuts (ADR-113 §5); the modals themselves are the shared
 * ContractActionModals — one implementation per verb.
 *
 * Built as a 1..N CARD GRID (one card per contract in force) inside the
 * standard tab shell (FormContainer + FormSection + ResultCount), using
 * this form's own ProjectCard idiom: a flat #f8f9fa Card, hair border, no
 * shadow, title + status chip on one line, and the facts as a two-column
 * label/value grid. Status is carried by the Active/Ended chip rather than
 * a colour stripe. Today the invariant is at most one open episode, so N
 * is 0 or 1 — under ADR-144 concurrent contracts simply add cards, no
 * restructure.
 *
 * Term NAMES, status and the action gates are read PER CONTRACT from
 * `employee.contracts[]` (ADR-156 §5 — one entry per contract in force,
 * primary first, matched here by episode id). The person's flat fields
 * are the PRIMARY contract's (ADR-156 §2) and are kept only as the
 * fallback for a payload that predates contracts[]. With one contract the
 * two agree; with two open contracts the second card would otherwise
 * show the primary's type, entity and status — invented facts.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Chip, Button, Grid, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { employeesApi, employmentApi } from '../../../../../services/api';
import { addContract } from '../../../../../services/event-handlers/employment';
import { useAlert } from '../../../../../contexts/AlertContext';
import EmptyState from '../../../../common/EmptyState';
import LoadingSpinner from '../../../../common/LoadingSpinner';
// The tab shell every other tab uses (Employment Episodes is the closest
// sibling): FormContainer + FormSection give the white panel, ResultCount
// the "N found" line above the list.
import { FormContainer, ResultCount } from '../EmploymentEpisodes/styles';
import { FaFileSignature } from 'react-icons/fa';
import { FormSection } from '../../styles';
import { COLORS } from '../../../../../constants/radar';
import { useAbility } from '../../../../../contexts/AbilityContext';
import { useAuth } from '../../../../../contexts/AuthContext';
import { isLockedBySource, sourceSystemLabel, sourceLockNote } from '../../../../../constants/employmentSourceSystems';
import { ACTIONS, SUBJECTS } from '../../../../../constants/permissions';
import { EMPLOYMENT_EVENT_TYPES } from '../../../../../constants/employmentEventTypes';
import { findPendingRenewalEvent } from '../../../../../constants/employmentEpisodes';
import { EVENT_VALIDATION_STATES } from '../../../../../constants/employmentValidationStates';
import {
  employmentEventAnnotation,
  employmentEventParts,
  eventDayFmt,
  EVENT_PART_KINDS
} from '../../../../../constants/employmentEventAnnotations';
import { ORGANIZATIONAL_STATUSES } from '../../../../../constants/organizationalStatuses';
import { useEmployeePermissions } from '../../../../../hooks/useEmployeePermissions';
// ADR-113 §5 — the shared lifecycle modals; this tab is their authoritative
// home, the card menu's shortcuts are the other mount.
import ContractActionModals from '../../../components/ContractActionModals';
import { CONTRACT_ACTIONS, formatContractNumber, contractIdentityLabel } from '../../../../../constants/contractActions';
import { contractNumberGate } from '../../../../../utils/contractNumber';
// ADR-173 — the shared contract-term fields and their one configuration
import ContractTermsFields, {
  ContractTypeField, ContractCompanyField, EngagementModelField
} from '../../../components/ContractTermsFields';
import FixedTermFields from '../../../components/FixedTermFields';
// ADR-176 — revise a Pending amendment in place (admin)
import EditAmendmentDialog from '../../../components/EditAmendmentDialog';
import SuccessorPreviewDialog from '../../../components/SuccessorPreviewDialog';
import { canReviseAmendmentEvent } from '../../../../../constants/amendmentRevision';
import { CONTRACT_TERM_LABELS, fixedTermEndProblem } from '../../../../../constants/contractTerms';
import { FormControl, InputLabel, Select, MenuItem, Menu, Divider } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { END_REASON_LABELS } from '../../../../../constants/employmentEndReasons';
// ADR-175 — amendment scope: person facts land on every contract in force
import {
  isPersonScopedField,
  contractsInForceOn,
  isRadarOwnedContract,
  amendmentEffectiveBounds
} from '../../../../../constants/amendableFields';

dayjs.extend(utc);

// ADR-142 §10 — END_REASON_LABELS now lives in constants/employmentEndReasons.js (ADR-168).

const sourceRefOf = (episode) => {
  const payload = episode?.payload;
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  const ref = payload.sourceReference;
  return ref && typeof ref === 'object' && !Array.isArray(ref) ? ref : null;
};

const formatDate = (value) => (value ? dayjs.utc(value).format('DD/MM/YYYY') : '—');

// ADR-175 D8 — a contracts[] entry in the episode shape contractIdentityLabel
// reads (number from sourceReference, the contract's own type and company).
const contractAsEpisode = (contract) => ({
  startDate: contract?.startDate || null,
  payload: { sourceReference: contract?.contractNumber ? { contractId: contract.contractNumber } : {} },
  contractType: contract?.contractType || null,
  contractCompany: contract?.contractCompany || null
});

// Mirror of the backend ownership predicate (ADR-143 Gate A's episode half):
// absent source = ours. Used only to HIDE the verb where the server would
// refuse it anyway; the §10 gates remain server-side and authoritative.
const isRadarOwnedRef = (ref) => !ref?.source || ref.source === 'radar';

const pendingAmendmentsOf = (episode) =>
  (episode?.employmentEvents || []).filter(
    (event) =>
      event.eventType?.code === EMPLOYMENT_EVENT_TYPES.CONTRACT_AMENDED &&
      event.eventValidationState === EVENT_VALIDATION_STATES.PENDING &&
      !event.processedAt
  );

// ADR-113 §6 — the per-contract facts, derived from THIS episode's own
// events rather than the person-level singleton OR (ADR-144 §10.4's rule,
// delivered a phase early where the data allows it). The Pending
// TERMINATION_FINALIZED is the same event-based fact as the API's
// hasPendingResignation — it stays true through the post-expiry grace
// window until the sweep enacts (ADR-113 §9.2), never a status test.
const pendingEndEventOf = (episode) =>
  (episode?.employmentEvents || []).find(
    (event) =>
      event.eventType?.code === EMPLOYMENT_EVENT_TYPES.TERMINATION_FINALIZED &&
      event.eventValidationState === EVENT_VALIDATION_STATES.PENDING
  ) || null;

// The scheduled replacement, on the episode that carries it — rendered and
// cancellable IN PLACE (ADR-113 §6), including on an already-ended contract
// (§9.1: the gapped-regime cancel must survive the contract's close).
// ADR-155 — one definition, shared with the lifecycle modal router so the
// event rendered here is the event the cancel request names.
const pendingRenewalEventOf = findPendingRenewalEvent;

// The route-amendable term fields (positionId deliberately absent — §9:
// position amendments come from the position history verb). Each field's
// scope (contract or person) comes from constants/amendableFields (ADR-175).
const AMENDABLE_FIELD_OPTIONS = [
  { value: 'engagementModelId', label: 'Engagement Model' },
  { value: 'contractCompanyId', label: 'Contract Company' },
  { value: 'contractTypeId', label: 'Contract Type' },
  // ADR-143 §4 revised: a name change formalized as an administrative
  // addendum — amendable, though not a contract term (it never enters the
  // §3 integrity set).
  { value: 'fullName', label: 'Full Name' }
];

// ADR-175 D8 — the trimmed, non-empty references of the contracts that get
// the amendment too.
const otherReferencesFor = (alsoOn, references) => Object.fromEntries(
  alsoOn
    .map((contract) => [contract.id, (references[contract.id] || '').trim()])
    .filter(([, reference]) => reference.length > 0)
);

/**
 * ADR-175 D8 — a person-scoped amendment (full name) lands on every contract
 * in force on its effective date. Said before submit, with one Addendum
 * reference field per other contract (each counterparty registers its own
 * addendum). Sync-owned contracts are named as not recorded here.
 */
const AmendmentReach = ({ reach, effectiveYmd, contractLabelOf, references, onReferenceChange }) => {
  if (!reach.thisInForce) {
    return (
      <Typography sx={{ fontSize: 13, color: '#B91C1C' }}>
        This contract is not in force on {formatDate(effectiveYmd)}. Record the change from a contract that is.
      </Typography>
    );
  }
  return (
    <Box sx={{ border: '1px solid #E2E8F0', borderRadius: 1, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {reach.alsoOn.length > 0 && (
        <>
          <Typography sx={{ fontSize: 13, color: '#334155', fontWeight: 600 }}>
            Also recorded on
          </Typography>
          {reach.alsoOn.map((contract) => (
            // The contract's identity is long (number · type · company
            // (country)), so it sits above the field as text, not inside a
            // label that would truncate it.
            <Box key={contract.id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography sx={{ fontSize: 13, color: '#475569', overflowWrap: 'anywhere' }}>
                {contractLabelOf(contract) || 'The other contract'}
              </Typography>
              <TextField
                size="small"
                label="Addendum reference (optional)"
                value={references[contract.id] || ''}
                onChange={(e) => onReferenceChange(contract.id, e.target.value)}
                fullWidth
              />
            </Box>
          ))}
        </>
      )}
      {reach.notOn.length > 0 && (
        <Typography sx={{ fontSize: 13, color: '#64748B' }}>
          Not recorded on {reach.notOn.map((contract) => contractLabelOf(contract) || 'a synced contract').join(', ')}: synced from its source system, which records its own addendum.
        </Typography>
      )}
    </Box>
  );
};

/**
 * ADR-143 §9 — the Amend Contract dialog. Field picker + the matching value
 * selector (Engagement Model was the §16 pilot; Company and Type joined
 * after it). An amendment declares WHAT changes, WHEN it takes effect and
 * WHY (§7): effective today or earlier it applies immediately; a future
 * date records a Pending amendment the sweep applies at that date.
 */
const AmendContractDialog = ({
  open, onClose, episode, currentTermNames, warnClusterId, onSubmitted,
  contracts = [], contractLabelOf = () => null, contract = null
}) => {
  const todayYmd = dayjs().format('YYYY-MM-DD');
  const [field, setField] = useState('engagementModelId');
  const [newValue, setNewValue] = useState('');
  const [effectiveOn, setEffectiveOn] = useState(dayjs());
  const [signedOn, setSignedOn] = useState(dayjs());
  const effectiveYmd = effectiveOn ? effectiveOn.format('YYYY-MM-DD') : null;
  const [reason, setReason] = useState('');
  const [externalReference, setExternalReference] = useState('');
  // ADR-175 D8 — the other contracts' Addendum references (episodeId → ref):
  // each counterparty registers its own addendum.
  const [otherReferences, setOtherReferences] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showAlert } = useAlert();

  // ADR-175 D8 — where a person-scoped amendment lands, said before submit.
  // Only worth saying when the person holds more than one contract in force.
  const personScoped = isPersonScopedField(field);
  const inForce = personScoped ? contractsInForceOn(contracts, effectiveYmd) : [];
  const reach = personScoped && inForce.length > 1
    ? {
      thisInForce: inForce.some((contract) => contract.id === episode?.id),
      alsoOn: inForce.filter((contract) => contract.id !== episode?.id && isRadarOwnedContract(contract)),
      notOn: inForce.filter((contract) => contract.id !== episode?.id && !isRadarOwnedContract(contract))
    }
    : null;

  // ADR-188 D3 — a contract fact takes effect while the contract runs; a
  // person fact must be recorded from a contract in force on its date.
  const bounds = amendmentEffectiveBounds({ contract, contracts, field, effectiveYmd, todayYmd });

  // The default date follows the contract the dialog opens on: today, or the
  // start of a contract that has not started yet.
  useEffect(() => {
    if (open) {
      setEffectiveOn(dayjs(bounds.defaultYmd));
    }
    // only on opening, or when the dialog moves to another contract
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, episode?.id]);

  const reset = () => {
    setField('engagementModelId');
    setNewValue('');
    setEffectiveOn(dayjs(bounds.defaultYmd));
    setSignedOn(dayjs());
    setReason('');
    setExternalReference('');
    setOtherReferences({});
  };

  const handleClose = () => {
    if (isSubmitting) return;
    reset();
    onClose();
  };

  const canSubmit = Boolean(typeof newValue === 'string' ? newValue.trim() : newValue)
    && reason.trim().length > 0
    && (!reach || reach.thisInForce)
    && !bounds.problem;
  const currentName = currentTermNames?.[field];

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const result = await employmentApi.createContractAmendment(episode.id, {
        changes: { [field]: newValue },
        ...(effectiveYmd ? { effectiveOn: effectiveYmd } : {}),
        ...(signedOn ? { signedOn: signedOn.format('YYYY-MM-DD') } : {}),
        reason: reason.trim(),
        ...(externalReference.trim() ? { externalReference: externalReference.trim() } : {}),
        ...(reach ? { externalReferences: otherReferencesFor(reach.alsoOn, otherReferences) } : {})
      });
      const applied = result?.state === 'Valid';
      const others = Array.isArray(result?.alsoRecordedOn) ? result.alsoRecordedOn.length : 0;
      const also = others > 0 ? ` on this contract and on ${others} other contract${others === 1 ? '' : 's'}` : '';
      showAlert(
        applied
          ? `Amendment #${result.amendmentNumber} recorded${also} and applied`
          : `Amendment #${result.amendmentNumber} recorded${also} — takes effect ${formatDate(effectiveYmd)} (cancellable until then)`,
        'success'
      );
      reset();
      onSubmitted();
    } catch (error) {
      showAlert(error.message || 'Failed to record the amendment', 'error', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Amend Contract</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
        <Typography sx={{ fontSize: 14, color: '#64748B' }}>
          {personScoped
            ? 'Records a contract amendment on every contract in force on the effective date'
            : 'Records a contract amendment on this contract'}
          {currentName ? ` (current ${AMENDABLE_FIELD_OPTIONS.find((o) => o.value === field)?.label.toLowerCase()}: ${currentName})` : ''}.
        </Typography>
        <FormControl fullWidth>
          <InputLabel>Term to amend</InputLabel>
          <Select
            value={field}
            label="Term to amend"
            onChange={(e) => { setField(e.target.value); setNewValue(''); }}
          >
            {AMENDABLE_FIELD_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {field === 'engagementModelId' && (
          <EngagementModelField
            value={newValue}
            onChange={setNewValue}
            label="New Engagement Model"
            required
          />
        )}
        {field === 'contractCompanyId' && (
          // The ajar mode (ADR-142 §8): every company selectable, cross-cluster
          // picks warn instead of being filtered out — with the same chips as
          // Hire and Add Contract (ADR-173 D1).
          <ContractCompanyField
            value={newValue}
            onChange={setNewValue}
            label="New Contract Company"
            warnClusterId={warnClusterId || null}
          />
        )}
        {field === 'contractTypeId' && (
          <ContractTypeField
            value={newValue}
            onChange={setNewValue}
            label="New Contract Type"
          />
        )}
        {field === 'fullName' && (
          <TextField
            label="New Full Name"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            required
            fullWidth
            helperText="An administrative addendum — records the name change on the contract"
          />
        )}
        <DatePicker
          label="Effective on"
          value={effectiveOn}
          onChange={(nextValue) => {
            if (nextValue && typeof nextValue.isValid === 'function' && nextValue.isValid()) {
              setEffectiveOn(nextValue);
            } else if (nextValue === null) {
              setEffectiveOn(null);
            }
          }}
          format="DD/MM/YYYY"
          minDate={bounds.minYmd ? dayjs(bounds.minYmd) : undefined}
          maxDate={bounds.maxYmd ? dayjs(bounds.maxYmd) : undefined}
          slotProps={{
            textField: {
              fullWidth: true,
              error: Boolean(bounds.problem),
              helperText: bounds.problem
                || (effectiveYmd && effectiveYmd > todayYmd
                  ? 'Future date — the amendment stays pending and applies on this date'
                  : 'Today or earlier — the amendment applies immediately')
            }
          }}
        />
        <DatePicker
          label="Signed on"
          value={signedOn}
          onChange={(nextValue) => {
            if (nextValue && typeof nextValue.isValid === 'function' && nextValue.isValid()) {
              setSignedOn(nextValue);
            } else if (nextValue === null) {
              setSignedOn(null);
            }
          }}
          format="DD/MM/YYYY"
          slotProps={{
            textField: { fullWidth: true }
          }}
        />
        <TextField
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
          multiline
          minRows={2}
          fullWidth
        />
        <TextField
          label={reach ? 'Addendum reference on this contract (optional)' : 'Addendum reference (optional)'}
          value={externalReference}
          onChange={(e) => setExternalReference(e.target.value)}
          helperText="The addendum's own registration number, if it has one — recorded verbatim"
          fullWidth
        />
        {reach && (
          <AmendmentReach
            reach={reach}
            effectiveYmd={effectiveYmd}
            contractLabelOf={contractLabelOf}
            references={otherReferences}
            onReferenceChange={(contractId, value) =>
              setOtherReferences((current) => ({ ...current, [contractId]: value }))}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || isSubmitting}>
          {isSubmitting ? 'Recording…' : 'Record Amendment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** §7.1 — cancel a Pending amendment (reason required; audited server-side). */
const CancelAmendmentDialog = ({ open, onClose, episodeId, amendmentEvent, onCancelled }) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showAlert } = useAlert();

  const handleClose = () => {
    if (isSubmitting) return;
    setReason('');
    onClose();
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await employmentApi.cancelContractAmendment(episodeId, amendmentEvent.id, { reason: reason.trim() });
      showAlert('Amendment cancelled', 'success');
      setReason('');
      onCancelled();
    } catch (error) {
      showAlert(error.message || 'Failed to cancel the amendment', 'error', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Cancel Amendment</DialogTitle>
      <DialogContent sx={{ pt: '12px !important' }}>
        <Typography sx={{ fontSize: 14, color: '#64748B', mb: 2 }}>
          {amendmentEvent ? employmentEventAnnotation(amendmentEvent) : ''}
        </Typography>
        <TextField
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
          multiline
          minRows={2}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>Keep it</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={!reason.trim() || isSubmitting}
        >
          {isSubmitting ? 'Cancelling…' : 'Cancel Amendment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * ADR-171 C4 (ADR-144 §17) — Add Contract: another contract beside the
 * primary, within the person's Region limit (ADR-167). When the primary has
 * a scheduled end, the new contract must end on or before it (ADR-153 R1) —
 * the end date becomes required and capped; the server re-checks.
 *
 * ADR-173 — built from the SAME fields as Hire / Rehire: ContractTermsFields
 * (labels, company chips, required markers) and FixedTermFields (empty end =
 * open-ended, auto-generated fixed-term comment). It may not start before the
 * primary (D6), mirrored here from the server refusal.
 */
const EMPTY_TERMS = { contractType: '', contractCompany: '', engagementModel: '', contractNumber: '' };

const AddContractDialog = ({ open, onClose, employee, primaryStartYmd, primaryEndYmd, onDone }) => {
  const [startYmd, setStartYmd] = useState(null);
  const [terms, setTerms] = useState(EMPTY_TERMS);
  const [endYmd, setEndYmd] = useState(null);
  const [endComment, setEndComment] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Remount key: resets FixedTermFields' own "comment edited" flag with the form
  const [formKey, setFormKey] = useState(0);
  const { showAlert } = useAlert();

  const mustEnd = Boolean(primaryEndYmd);
  const lastStartYmd = mustEnd ? dayjs.utc(primaryEndYmd).subtract(1, 'day').format('YYYY-MM-DD') : null;
  const startProblem = startYmd && primaryStartYmd && startYmd < primaryStartYmd
    ? `Must be on or after ${dayjs.utc(primaryStartYmd).format('DD/MM/YYYY')}, the primary contract's start`
    : (startYmd && lastStartYmd && startYmd > lastStartYmd
      ? `Must be before ${dayjs.utc(primaryEndYmd).format('DD/MM/YYYY')}, the primary contract's end`
      : null);
  const endProblem = fixedTermEndProblem({ startYmd, endYmd, maxEndYmd: primaryEndYmd });
  const canSubmit = Boolean(startYmd && !startProblem && terms.contractType && terms.contractCompany
    && (!mustEnd || endYmd)
    && (!endYmd || (endComment.trim() && !endProblem)));

  const handleTermChange = useCallback((field, value) => {
    setTerms((prev) => ({ ...prev, [field]: value }));
  }, []);

  const reset = () => {
    setStartYmd(null); setTerms(EMPTY_TERMS); setEndYmd(null); setEndComment(''); setReason('');
    setFormKey((k) => k + 1);
  };

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await addContract({
        employeeId: employee.id,
        contractStartDate: startYmd,
        contractType: terms.contractType,
        contractCompany: terms.contractCompany,
        ...(terms.engagementModel ? { engagementModel: terms.engagementModel } : {}),
        ...(terms.contractNumber.trim() ? { contractNumber: terms.contractNumber.trim() } : {}),
        ...(endYmd ? { contractEndDate: endYmd, contractEndReason: endComment.trim() } : {}),
        ...(reason.trim() ? { reason: reason.trim() } : {})
      });
      showAlert('Contract added', 'success');
      reset();
      onDone();
    } catch (error) {
      showAlert(error.message || 'Failed to add the contract', 'error', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Contract</DialogTitle>
      <DialogContent key={formKey} sx={{ pt: '12px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography sx={{ fontSize: 14, color: '#475569' }}>
          A further contract beside the primary one. It joins as a secondary contract: the
          individual&apos;s status, position and other contracts do not change, and it ends at the
          latest when the primary contract ends.
          {employee?.openContractLimit?.regionName
            ? ` ${employee.openContractLimit.regionName} allows ${employee.openContractLimit.max} open contracts per individual.`
            : ''}
        </Typography>
        <DatePicker
          label={CONTRACT_TERM_LABELS.contractStartDate}
          value={startYmd ? dayjs.utc(startYmd) : null}
          onChange={(value) => {
            if (value && typeof value.isValid === 'function' && value.isValid()) {
              setStartYmd(value.format('YYYY-MM-DD'));
            } else if (value === null) {
              setStartYmd(null);
            }
          }}
          format="DD/MM/YYYY"
          minDate={primaryStartYmd ? dayjs.utc(primaryStartYmd) : undefined}
          maxDate={lastStartYmd ? dayjs.utc(lastStartYmd) : undefined}
          slotProps={{
            textField: {
              fullWidth: true,
              required: true,
              error: Boolean(startProblem),
              helperText: startProblem || undefined,
              inputProps: { placeholder: 'DD/MM/YYYY' }
            }
          }}
        />
        <ContractTermsFields
          values={terms}
          onChange={handleTermChange}
          warnClusterId={employee?.cluster?.id || null}
        />
        <FixedTermFields
          startYmd={startYmd}
          endYmd={endYmd}
          onEndChange={setEndYmd}
          comment={endComment}
          onCommentChange={setEndComment}
          contractNumber={terms.contractNumber}
          endRequired={mustEnd}
          maxEndYmd={primaryEndYmd}
          endHelperText={mustEnd
            ? `Required: the primary contract ends on ${dayjs.utc(primaryEndYmd).format('DD/MM/YYYY')}`
            : null}
        />
        <TextField
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          multiline
          minRows={2}
          fullWidth
          helperText="Optional, recorded with the new contract"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || isSubmitting}>
          {isSubmitting ? 'Adding…' : 'Add Contract'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * ADR-171 C1 (ADR-144 §5, ADR-153 R4) — make another open contract the
 * person's primary. A deliberate, audited act: the person's status, their
 * contract fields and their scheduled end follow the primary from then on.
 */
const MakePrimaryDialog = ({ open, onClose, employeeId, episode, contractLabel, onDone }) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showAlert } = useAlert();

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    setReason('');
    onClose();
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await employeesApi.designatePrimaryContract(employeeId, episode.id, reason.trim() || undefined);
      showAlert('Primary contract changed', 'success');
      setReason('');
      onDone();
    } catch (error) {
      showAlert(error.message || 'Failed to make this contract the primary', 'error', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Make Primary</DialogTitle>
      <DialogContent sx={{ pt: '12px !important' }}>
        <Typography sx={{ fontSize: 14, color: '#475569', mb: 2 }}>
          {contractLabel || 'This contract'} becomes the primary contract. The person&apos;s status,
          contract fields and scheduled end follow it from now on, and closing it later ends every
          other contract the person holds. Nothing is promoted automatically: this is the only way
          the primary moves.
        </Typography>
        <TextField
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          multiline
          minRows={2}
          fullWidth
          helperText="Optional, recorded with the change"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting || !episode}>
          {isSubmitting ? 'Saving…' : 'Make Primary'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * ADR-149 §7 — "What happens next": every future-dated fact on this contract
 * as ONE list in date order.
 *
 * It replaces three same-coloured panels (pending amendments, scheduled
 * replacement, scheduled end) that were grouped by TYPE, which destroyed the
 * only ordering that matters. The three panels put a surname correction and
 * the end of someone's employment on the same #fefce8, in an order that ran
 * 30/09 → 01/10 → 28/08 down the card; read top to bottom this list is the
 * contract's future as a sentence.
 *
 * What did NOT change, deliberately: which events appear (the caller's three
 * predicates are untouched), their gates, and the rule that each action sits
 * beside the thing it acts on — ADR-113 §6, the placement is the warning.
 */

// kind → colour. The palette lives with the component that paints; the parts
// function stays presentation-free so other surfaces can reuse it.
const ROW_TONE = {
  [EVENT_PART_KINDS.AMENDMENT]:    { dot: '#D97706', chipBg: '#FEF3C7', chipText: '#92400E' },
  [EVENT_PART_KINDS.CONTRACT_END]: { dot: '#DC2626', chipBg: '#FEE2E2', chipText: '#991B1B' },
  [EVENT_PART_KINDS.SUCCESSOR]:    { dot: '#2563EB', chipBg: '#DBEAFE', chipText: '#1E3A8A' }
};
const NEUTRAL_TONE = { dot: '#94a3b8', chipBg: '#f1f5f9', chipText: '#475569' };

const TimelineRail = ({ colour, isFirst, isLast }) => (
  <Box sx={{
    position: 'relative',
    alignSelf: 'stretch',
    // The connecting line overshoots the row's padding so consecutive rows
    // join; the first and last rows clip their outer half.
    '&::before': {
      content: '""',
      position: 'absolute',
      left: '5px',
      top: isFirst ? '9px' : '-14px',
      ...(isLast ? { height: '9px' } : { bottom: '-14px' }),
      width: '1px',
      backgroundColor: '#e2e8f0'
    }
  }}>
    <Box sx={{
      position: 'absolute',
      left: 0,
      top: '4px',
      width: '11px',
      height: '11px',
      borderRadius: '50%',
      backgroundColor: colour,
      border: '2px solid #fff',
      boxShadow: `0 0 0 1px ${colour}`
    }} />
  </Box>
);

// ADR-149 §7 — the constructive tier: outlined and NEUTRAL, not MUI blue.
// Blue reads as "primary" and competed with the one action that is.
const GHOST_SX = {
  textTransform: 'none',
  fontSize: '14px',
  height: '36px',
  color: '#475569',
  borderColor: '#cbd5e1',
  '&:hover': { borderColor: '#94a3b8', backgroundColor: '#f8fafc' },
  '&.Mui-disabled': { color: '#94a3b8', borderColor: '#e2e8f0' }
};

// ADR-149 §7 put the joining terminals behind a "More actions ▾" menu here;
// ADR-158 moved both to the person surfaces (they void the PERSON's joining,
// not one contract's — §3). The menu is back (ADR-159 §4 C5) with the
// contract-level Void Contract, red inside the menu, with an ellipsis,
// exactly as §7 specified. It appears only for a joining, non-primary,
// radar-owned contract beside another open one — never before ADR-144
// phase 5 lets a person hold two.

const ScheduledTimeline = ({ rows }) => {
  if (!rows || rows.length === 0) return null;

  // Splice the anchor in at its own date rather than pinning it to the top.
  const todayKey = dayjs.utc().format('YYYY-MM-DD');
  // >= not >: every row in this list is a PENDING event, so a row dated
  // today has not been enacted — the sweeps run on weekday mornings, and a
  // scheduled end waits out the grace window on top of that. Placing it
  // above the anchor would show a change that is merely due as one that has
  // already happened. Only genuinely past dates sort above Today, which is
  // the ADR-113 §9.2 grace-window case the anchor was made movable for.
  const firstAhead = rows.findIndex((row) => !row.parts.date || row.parts.date >= todayKey);
  const at = firstAhead === -1 ? rows.length : firstAhead;
  const items = [...rows.slice(0, at), { anchor: true }, ...rows.slice(at)];

  return (
    <Box sx={{
      mt: 2,
      backgroundColor: '#fff',
      border: '1px solid #e3e8ef',
      borderRadius: '4px',
      overflow: 'hidden'
    }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 1.5,
        px: 1.75,
        py: 1.25,
        backgroundColor: '#f4f6f9',
        borderBottom: '1px solid #e3e8ef'
      }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
          What happens next
        </Typography>
        <Typography sx={{ fontSize: 12, color: '#64748B', fontVariantNumeric: 'tabular-nums' }}>
          {rows.length} scheduled {rows.length === 1 ? 'change' : 'changes'}
        </Typography>
      </Box>

      <Box sx={{ px: 1.75, pt: 0.5, pb: 1.5 }}>
        {items.map((item, index) => {
          const isLast = index === items.length - 1;

          // The anchor. Without a "today" the dates are absolute values the
          // reader has to convert into distances themselves — and it sits in
          // its CHRONOLOGICAL place, not always first: inside the ADR-113
          // §9.2 grace window a contract's end date has already passed while
          // its verbs stay live, and pinning the anchor to the top would
          // render a past event as though it were still ahead.
          if (item.anchor) {
            return (
              <Box
                key="today"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '84px 16px minmax(0, 1fr)',
                  gap: 1.25,
                  alignItems: 'start',
                  py: 1.25,
                  ...(index > 0 ? { borderTop: '1px solid #f1f5f9' } : {})
                }}
              >
                <Typography sx={{ fontSize: 12.5, color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
                  {dayjs().format('DD/MM/YYYY')}
                </Typography>
                <TimelineRail colour={NEUTRAL_TONE.dot} isFirst={index === 0} isLast={isLast} />
                <Typography sx={{
                  fontSize: 10.5,
                  letterSpacing: '.09em',
                  textTransform: 'uppercase',
                  color: '#94a3b8'
                }}>
                  Today
                </Typography>
              </Box>
            );
          }

          const row = item;

          const tone = ROW_TONE[row.parts.kind] || NEUTRAL_TONE;
          return (
            <Box
              key={row.key}
              sx={{
                display: 'grid',
                gridTemplateColumns: '84px 16px minmax(0, 1fr)',
                gap: 1.25,
                alignItems: 'start',
                py: 1.25,
                ...(index > 0 ? { borderTop: '1px solid #f1f5f9' } : {})
              }}
            >
              <Typography sx={{
                fontSize: 12.5,
                fontWeight: 600,
                color: '#334155',
                fontVariantNumeric: 'tabular-nums',
                pt: '1px'
              }}>
                {row.parts.dateLabel || '—'}
              </Typography>

              <TimelineRail colour={tone.dot} isFirst={index === 0} isLast={isLast} />

              {/* Body and actions share a wrapping flex row: a lone Cancel
                  sits on the caption's line, while the end-date trio drops
                  to its own line in a half-width card rather than squeezing. */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      mb: '3px',
                      px: '5px',
                      borderRadius: '2px',
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '.07em',
                      textTransform: 'uppercase',
                      backgroundColor: tone.chipBg,
                      color: tone.chipText
                    }}
                  >
                    {row.parts.label}
                  </Box>
                  <Typography sx={{ fontSize: 13, color: '#1e293b' }}>
                    {row.parts.headline}
                  </Typography>
                  {row.parts.meta.length > 0 && (
                    <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: '2px' }}>
                      {row.parts.meta.join(' · ')}
                    </Typography>
                  )}
                </Box>

                {row.actions.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flexShrink: 0, alignItems: 'center' }}>
                    {row.actions.map((action, actionIndex) => {
                      // A hairline between every pair (owner's call,
                      // 2026-08-28). Text buttons carry no border of their
                      // own, so on a row of three the gaps alone left them
                      // reading as one strip; the rules make each verb a
                      // discrete target. Never leading — a lone Cancel on an
                      // amendment row gets no rule hanging off nothing.
                      const needsRule = actionIndex > 0;
                      return (
                        <React.Fragment key={action.label}>
                          {needsRule && (
                            <Box
                              aria-hidden
                              sx={{ width: '1px', height: '16px', backgroundColor: '#e2e8f0', mx: 0.25 }}
                            />
                          )}
                          <Button
                            size="small"
                            color={action.tone === 'error' ? 'error' : 'primary'}
                            // Sentence case, matching the action row below.
                            // MUI's default uppercase erases word boundaries,
                            // which is what made three verbs read as one strip.
                            sx={{ minWidth: 'auto', textTransform: 'none', fontSize: 13 }}
                            onClick={action.onClick}
                          >
                            {action.label}
                          </Button>
                        </React.Fragment>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// ProjectCard's definition grid: labels in column 1 (bold, darker), values
// in column 2. Rendered as sibling cells of one CSS grid — hence the
// fragment rather than a wrapper Box.
const Row = ({ label, children }) => (
  <>
    <div>{label}:</div>
    <div>{children}</div>
  </>
);

const ContractDetails = ({ employeeId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [amendDialogEpisode, setAmendDialogEpisode] = useState(null);
  const [cancelDialogTarget, setCancelDialogTarget] = useState(null); // { episodeId, event }
  const [editAmendmentTarget, setEditAmendmentTarget] = useState(null); // { episodeId, event } — ADR-176
  const [successorPreviewTarget, setSuccessorPreviewTarget] = useState(null); // { episode, event, canAct } — ADR-180
  // ADR-113 — the lifecycle verb being performed: { action, episode }.
  const [lifecycleTarget, setLifecycleTarget] = useState(null);
  // ADR-159 §4.1 — the contract card's "More actions ▾" menu (ADR-149 §7)
  const [moreActions, setMoreActions] = useState(null); // { anchorEl, episode }
  const [primaryDialogEpisode, setPrimaryDialogEpisode] = useState(null);
  const [addContractOpen, setAddContractOpen] = useState(false);
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const ability = useAbility();
  // ADR-183 — only an admin may change a sync-owned contract
  const { user } = useAuth();
  const viewerIsAdmin = Boolean(user?.employee?.isAdmin);
  const { canManageEmployee } = useEmployeePermissions();
  const canCreateAmendment = ability.can(ACTIONS.CREATE, SUBJECTS.CONTRACT_AMENDMENT);
  const canCancelAmendment = ability.can(ACTIONS.UPDATE, SUBJECTS.CONTRACT_AMENDMENT);
  // ADR-176 — Edit on a Pending amendment: admin-only (both rights)
  const canReviseAmendment = canCancelAmendment
    && ability.can(ACTIONS.UPDATE, SUBJECTS.CONTRACT_AMENDMENT_REVISION);
  // ADR-171 C1 — the right behind "Make Primary"
  const canDesignatePrimary = ability.can(ACTIONS.UPDATE, SUBJECTS.EMPLOYEE_PRIMARY_CONTRACT);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      const [employeeData, episodesData] = await Promise.all([
        employeesApi.getEmployeeById(employeeId),
        employmentApi.getEmploymentEpisodes(employeeId),
      ]);
      setEmployee(employeeData);
      setEpisodes(Array.isArray(episodesData) ? episodesData : []);
    } catch (error) {
      showAlert('Failed to load contract details', 'error', error);
    } finally {
      setIsLoading(false);
    }
  }, [employeeId, showAlert]);

  useEffect(() => { load(); }, [load]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  // §5.5 — the contract(s) in force: every open episode; for a person with
  // none (resigned / cancelled), the most recent episode — their last
  // contract, shown ended.
  const openEpisodes = episodes.filter((episode) => !episode.endDate);
  const latestEpisode = [...episodes].sort((a, b) => {
    if (!a.endDate !== !b.endDate) return a.endDate ? 1 : -1; // open first
    return new Date(b.startDate) - new Date(a.startDate);
  })[0] || null;
  // ADR-156 §5's order, the same as contracts[]: the PRIMARY first, then
  // the other open contracts by start date, newest first. The primary is
  // the person-level anchor, so it leads the grid.
  const primaryEpisodeId = (Array.isArray(employee?.contracts) ? employee.contracts : [])
    .find((contract) => contract.isPrimary)?.id ?? null;
  const orderedOpenEpisodes = [...openEpisodes].sort((a, b) => {
    const aPrimary = a.id === primaryEpisodeId;
    const bPrimary = b.id === primaryEpisodeId;
    if (aPrimary !== bPrimary) return aPrimary ? -1 : 1;
    return new Date(b.startDate) - new Date(a.startDate);
  });
  const displayEpisodes = orderedOpenEpisodes.length > 0
    ? orderedOpenEpisodes
    : (latestEpisode ? [latestEpisode] : []);
  // ADR-156 §5 — this person's contracts[] by episode id. The list is the
  // same population as displayEpisodes (open ones, else the latest), so a
  // miss means an older API payload — the flat fields then stand in.
  const contractsById = new Map(
    (Array.isArray(employee?.contracts) ? employee.contracts : []).map((contract) => [contract.id, contract])
  );

  if (displayEpisodes.length === 0) {
    return (
      <FormContainer>
        <FormSection>
          <EmptyState
            title="No Contracts Found"
            description="This individual doesn't have any contracts recorded yet."
          />
        </FormSection>
      </FormContainer>
    );
  }

  const renderContract = (episode) => {
    const ref = sourceRefOf(episode);
    const isOpen = !episode.endDate;
    // Hide the verb where the server-side §10 gate would refuse anyway
    // (sync-owned contract); the gates stay authoritative server-side.
    const radarOwned = isRadarOwnedRef(ref);
    const pendingAmendments = pendingAmendmentsOf(episode);
    // ADR-113 §6 — this contract's own facts drive its action set.
    // ADR-183 — a sync-owned contract is read-only to everyone but an admin
    // (superseding ADR-113 §9.4's proceed-with-warning): no lifecycle verb,
    // no timeline action. The server refuses the same (SYNC_OWNED_CONTRACT).
    const lockedBySource = isOpen && isLockedBySource(ref, viewerIsAdmin);
    const pendingEnd = pendingEndEventOf(episode);
    const pendingRenewal = pendingRenewalEventOf(episode);
    const canAct = canManageEmployee(employee) && !lockedBySource;
    // ADR-156 §5.1 — THIS contract's own status and gates (never the
    // person's, which are the primary's). Flat-field fallback only for a
    // payload without contracts[].
    const contract = contractsById.get(episode.id) || null;
    const status = contract ? contract.status : employee?.organizationalStatus;
    const contractType = contract ? contract.contractType : employee?.contractType;
    const contractCompany = contract ? contract.contractCompany : employee?.contractCompany;
    const engagementModel = contract ? contract.engagementModel : employee?.engagementModel;
    const termEndDate = contract ? contract.termEndDate : employee?.contractEndDate;
    const isPrimary = Boolean(contract?.isPrimary);

    // ADR-149 §7 — the gates, re-expressed as ELIGIBILITY plus a BLOCKING
    // REASON. The ENABLED set is bit-for-bit what it was (ADR-113 §8's
    // per-contract re-derivation of the ADR-136 mutual-exclusion web): Add
    // End Date needs no pending end and no scheduled replacement, Replace
    // needs no scheduled replacement. What is new is that a verb suppressed
    // by a schedule renders DISABLED WITH THE REASON instead of vanishing
    // and leaving the operator to guess where it went.
    //
    // Resign is absent by ADR-149 §1: a departure ends every contract the
    // person holds, so it is a person verb. Its homes are the repository
    // card's menu and the form header's person action area (ADR-149 §5).
    const addEndDateEligible = canAct && isOpen && status === ORGANIZATIONAL_STATUSES.EMPLOYED;
    const addEndDateBlockedBy = pendingEnd
      ? 'an end date is already scheduled'
      : (pendingRenewal ? 'a replacement is scheduled' : null);
    const replaceEligible = canAct && isOpen
      && (status === ORGANIZATIONAL_STATUSES.EMPLOYED || status === ORGANIZATIONAL_STATUSES.LEAVING);
    const replaceBlockedBy = pendingRenewal ? 'a replacement is already scheduled' : null;
    // ADR-158 §3.3 — Start Delayed is the one joining verb that addresses a
    // contract: two joining contracts may start on different days, so moving
    // one is a complete act on that contract. Its home is here, and since
    // ADR-158 §7.4 only here (the card entry retired — ADR-153 decision 14).
    // Hire Withdraw and Mark No Show are absent by ADR-158 §1: voiding the
    // hire voids the PERSON's joining — every not-started contract — so they
    // are person verbs, homed with Resign on the card menu and the form
    // header's person action area.
    const showStartDelayed = canAct && isOpen && status === ORGANIZATIONAL_STATUSES.JOINING;
    const blockedNotes = [
      addEndDateEligible && addEndDateBlockedBy ? `Add End Date — ${addEndDateBlockedBy}` : null,
      replaceEligible && replaceBlockedBy ? `Replace Contract — ${replaceBlockedBy}` : null
    ].filter(Boolean);
    const canAmend = isOpen && radarOwned && canCreateAmendment;
    // ADR-159 §4.1 (C5) — Void Contract: a contract that has not started,
    // is not the primary (Q4), is radar-owned, and is not the person's only
    // open contract (otherwise it is the person's Hire Withdraw). The server
    // re-checks every gate, plus the hire-withdrawal cutoff (Q3).
    const voidEligible = canAct && isOpen && radarOwned && !isPrimary
      && status === ORGANIZATIONAL_STATUSES.JOINING && openEpisodes.length > 1;
    // ADR-171 C1 — "Make Primary": another open contract beside the primary.
    // ADR-183 — not locked by the source: which contract is primary is radar's
    // own designation, not a change to the synced record.
    const makePrimaryEligible = canManageEmployee(employee) && canDesignatePrimary && isOpen && !isPrimary
      && openEpisodes.length > 1;
    // ADR-174 §5.2/§5.3 — radar-owned contract + the right for its state
    // (closed contracts included, under the correction right)
    const numberGate = contractNumberGate(ability, episode);
    const hasContractNumber = ref?.contractId !== undefined && ref?.contractId !== null
      && String(ref.contractId).trim() !== '';
    // The overflow menu holds the contract's rarely used actions: the
    // contract number (not an amendment — a reference, ADR-174), Make
    // Primary (ADR-171 C1; kept out of sight on purpose, owner 2026-10-12)
    // and Void Contract. It appears whenever at least one of them is
    // allowed, so a closed contract still has its row, with the menu alone.
    const hasMoreActions = numberGate.allowed || makePrimaryEligible || voidEligible;
    const hasActionRow = canAmend || addEndDateEligible || replaceEligible
      || showStartDelayed || hasMoreActions;

    const openLifecycle = (action) => setLifecycleTarget({ action, episode });

    // ADR-149 §7 — the three panels become one dated list. The three
    // predicates feeding it are UNCHANGED, so which events appear, and the
    // gates they carry, are exactly what the panels showed:
    //   · amendments — pending and unprocessed;
    //   · the scheduled end — keeps its isOpen gate (a closed contract's end
    //     has already happened);
    //   · the scheduled replacement — deliberately NOT isOpen-gated (ADR-113
    //     §9.1: the gapped-regime cancel must survive the contract's close).
    // What changed is that they are now sorted against each other.
    const timelineRow = (event, kind, label, actions) => ({
      key: `${kind}-${event.id}`,
      // A row whose caption could not be written must still render: the
      // caption is optional, the action is not — dropping the row would
      // strand a cancellable event with no way to reach it.
      parts: employmentEventParts(event) || {
        kind,
        label,
        headline: 'Scheduled change',
        meta: [],
        date: event.effectiveOn || null,
        dateLabel: eventDayFmt(event.effectiveOn)
      },
      actions
    });

    const scheduledRows = [
      ...pendingAmendments.map((event) => timelineRow(
        event,
        EVENT_PART_KINDS.AMENDMENT,
        'Amendment',
        [
          // ADR-176 — Edit sits before Cancel: the lighter act first.
          ...(canReviseAmendment && isOpen && radarOwned && canReviseAmendmentEvent(event)
            ? [{
                label: 'Edit',
                onClick: () => setEditAmendmentTarget({ episodeId: episode.id, event })
              }]
            : []),
          ...(canCancelAmendment
            ? [{
                label: 'Cancel',
                tone: 'error',
                onClick: () => setCancelDialogTarget({ episodeId: episode.id, event })
              }]
            : [])
        ]
      )),
      ...(isOpen && pendingEnd
        ? [timelineRow(pendingEnd, EVENT_PART_KINDS.CONTRACT_END, 'Contract ends',
            canAct
              ? [
                  { label: 'Change date', onClick: () => openLifecycle(CONTRACT_ACTIONS.CHANGE_END_DATE) },
                  { label: 'Remove', onClick: () => openLifecycle(CONTRACT_ACTIONS.RESIGN_WITHDRAW) },
                  { label: 'Cancel contract', tone: 'error', onClick: () => openLifecycle(CONTRACT_ACTIONS.CANCEL_CONTRACT) }
                ]
              : [])]
        : []),
      ...(pendingRenewal
        ? [timelineRow(pendingRenewal, EVENT_PART_KINDS.SUCCESSOR, 'Successor contract',
            [
              // ADR-180 — read-only, so not gated by canAct: anyone who can
              // read the person can see what was scheduled. The lighter act
              // first, as ADR-176 placed Edit before Cancel.
              {
                label: 'View',
                onClick: () => setSuccessorPreviewTarget({ episode, event: pendingRenewal, canAct })
              },
              ...(canAct
                ? [{
                    label: 'Cancel renewal',
                    tone: 'error',
                    onClick: () => openLifecycle(CONTRACT_ACTIONS.CANCEL_SCHEDULED_RENEWAL)
                  }]
                : [])
            ])]
        : [])
    ].sort((a, b) => {
      // Chronological on the RAW date. Sorting the DD/MM/YYYY label instead
      // is the bug this list exists to fix — it puts 02/09 before 28/08.
      if (!a.parts.date) return 1;
      if (!b.parts.date) return -1;
      return a.parts.date.localeCompare(b.parts.date);
    });

    // The card's identity line: the contract's NUMBER when it has one —
    // never an ordinal (ADR-142 §11: under ADR-144 two live contracts must
    // be tellable apart, which an ordinal cannot do).
    // ADR-116 — rendered by the shared formatter as `Contract · 10002`.
    const title = formatContractNumber(ref?.contractId) || 'Contract';

    return (
      <Card
        key={episode.id}
        sx={{
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          width: '100%',
          height: '100%',
          border: '1px solid #e0e0e0',
          boxShadow: 'none'
        }}
      >
        <CardContent sx={{ height: '100%', p: 2, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: COLORS.PRIMARY }}>
                {title}
              </Typography>
              {/* ADR-174 §5 — the Contract Number verb lives in the card's
                  More actions ▾ menu (owner, 2026-10-12). A sync-owned
                  contract says who maintains its number instead. */}
              {!numberGate.allowed && numberGate.reason && (
                <Typography component="span" sx={{ fontSize: '12px', color: '#94a3b8' }}>
                  {numberGate.reason}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {/* ADR-156 §2 — the primary is a selected fact; name it only
                  when there is something to tell apart. */}
              {isPrimary && openEpisodes.length > 1 && (
                <Chip
                  label="Primary"
                  size="small"
                  sx={{ backgroundColor: '#e0e7ff', color: '#4f46e5', fontWeight: 500, fontSize: '12px' }}
                />
              )}
              <Chip
                label={isOpen ? 'Active' : 'Ended'}
                size="small"
                sx={{
                  backgroundColor: isOpen ? '#dcfce7' : '#fee2e2',
                  color: isOpen ? '#166534' : '#991b1b',
                  fontWeight: 500,
                  fontSize: '12px'
                }}
              />
              {!isOpen && episode.endReason && (
                <Chip
                  label={END_REASON_LABELS[episode.endReason] || episode.endReason}
                  size="small"
                  sx={{ backgroundColor: '#f1f5f9', color: '#475569', fontSize: '12px' }}
                />
              )}
            </Box>
          </Box>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '150px 1fr',
            gap: '8px',
            alignItems: 'start',
            fontSize: '13px',
            color: '#64748B',
            '& > *:nth-of-type(odd)': {
              fontWeight: 600,
              color: '#475569'
            }
          }}>
            <Row label="Contract Type">{contractType?.name || '—'}</Row>
            <Row label="Contract Company">
              {contractCompany?.name || '—'}
              {contractCompany?.country?.name
                ? ` (${contractCompany.country.name})` : ''}
            </Row>
            <Row label="Engagement Model">{engagementModel?.name || '—'}</Row>
            {/* ADR-143 §4: the position is a contract fact — "changing a
                function requires an addendum" — and is amendable via the
                position history verb. Grade is deliberately absent (not a
                contract term, §4/ADR-135 5A.10 d5). Position history is
                still a PERSON record (one timeline), so this stays the
                person's current position on every card — not in
                contracts[] (ADR-156 §7). */}
            <Row label="Position">{employee?.position?.label || '—'}</Row>
            {/* ADR-142 §11 — Contract Start Date, from the episode;
                deliberately NOT labelled Hire Date (that is the person's
                tenure anchor on the Employment Information tab — ADR-138). */}
            <Row label="Contract Start Date">{formatDate(episode.startDate)}</Row>
            {isOpen ? (
              // The agreed term end — the pending scheduled end (ADR-142
              // §6.3 branch 2 = the API's contractEndDate). Empty =
              // open-ended.
              <Row label="Contract End Date">
                {termEndDate ? formatDate(termEndDate) : 'Open-ended'}
              </Row>
            ) : (
              <Row label="Ended On">{formatDate(episode.endDate)}</Row>
            )}
            <Row label="Source">{sourceSystemLabel(ref?.source) || 'Radar'}</Row>
          </Box>

          <ScheduledTimeline rows={scheduledRows} />

          {/* ADR-183 — say why the actions are absent, for anyone who could
              otherwise manage this person */}
          {lockedBySource && canManageEmployee(employee) && (
            <Typography sx={{ mt: 2, fontSize: 12, color: '#64748B' }}>
              {sourceLockNote(ref?.source)}
            </Typography>
          )}

          {/* ADR-113 §6 / ADR-149 §7 — the contract's action row, in two
              tiers. One filled primary (Amend Contract: routine, reversible,
              most-used); constructive verbs outlined and neutral. Panel-local
              actions never appear here — Cancel, the end-date verbs and
              Cancel Renewal sit on their own timeline rows, where the
              placement is the warning. Rehire, Add Contract, Resign (ADR-149)
              and — since ADR-158 — Hire Withdraw and Mark No Show are person
              verbs and stay off the tab entirely. From this row, in any
              state, nothing can void or end. */}
          {hasActionRow && (
            <Box sx={{ mt: 'auto', pt: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {canAmend && (
                  <Button
                    variant="contained"
                    disableElevation
                    startIcon={<FaFileSignature />}
                    onClick={() => setAmendDialogEpisode(episode)}
                    sx={{
                      backgroundColor: COLORS.PRIMARY,
                      textTransform: 'none',
                      fontSize: '14px',
                      height: '36px',
                      '&:hover': { backgroundColor: '#23406f' }
                    }}
                  >
                    Amend Contract
                  </Button>
                )}
                {replaceEligible && (
                  <Button
                    variant="outlined"
                    sx={GHOST_SX}
                    disabled={Boolean(replaceBlockedBy)}
                    title={replaceBlockedBy || undefined}
                    // ADR-113 addendum — launched from the tab, the replace
                    // flow is FOCUSED (no tab bar) and both Back and
                    // completion return here, to the contract the operator
                    // was managing.
                    onClick={() => navigate(`/employees/${employeeId}/edit/replace?contract=${encodeURIComponent(episode.id)}`, {
                      state: {
                        // ADR-155 §6 — the contract being replaced rides to the flow
                        // (also in the URL, so a reload or shared link keeps it)
                        replaceEpisodeId: episode.id,
                        returnTo: `/employees/${employeeId}/edit?tab=CONTRACTS`,
                        returnLabel: 'Contracts'
                      }
                    })}
                  >
                    Replace Contract
                  </Button>
                )}
                {addEndDateEligible && (
                  <Button
                    variant="outlined"
                    sx={GHOST_SX}
                    disabled={Boolean(addEndDateBlockedBy)}
                    title={addEndDateBlockedBy || undefined}
                    onClick={() => openLifecycle(CONTRACT_ACTIONS.ADD_END_DATE)}
                  >
                    Add End Date
                  </Button>
                )}
                {showStartDelayed && (
                  <Button
                    variant="outlined"
                    sx={GHOST_SX}
                    onClick={() => openLifecycle(CONTRACT_ACTIONS.START_DELAYED)}
                  >
                    Start Delayed
                  </Button>
                )}
                {hasMoreActions && (
                  <Button
                    variant="outlined"
                    sx={GHOST_SX}
                    aria-haspopup="menu"
                    onClick={(event) => setMoreActions({
                      anchorEl: event.currentTarget,
                      episode,
                      canSetNumber: numberGate.allowed,
                      hasNumber: hasContractNumber,
                      canMakePrimary: makePrimaryEligible,
                      canVoid: voidEligible
                    })}
                  >
                    More actions ▾
                  </Button>
                )}
              </Box>
              {blockedNotes.length > 0 && (
                <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.75 }}>
                  {blockedNotes.join(' · ')}. Cancel the scheduled item above to re-enable.
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // ADR-156 — the amend dialog's "current" names are the AMENDED contract's,
  // not the primary's (flat-field fallback for a payload without contracts[]).
  const amendedContract = amendDialogEpisode ? (contractsById.get(amendDialogEpisode.id) || null) : null;
  const amendedTerms = amendedContract || employee || {};

  // ADR-171 C4 — Add Contract's gate and the R1 cap (the primary's own end).
  // Declared before `dialogs`, which reads primaryEndYmd when it is built.
  const primaryContract = (employee?.contracts || []).find((c) => c.isPrimary && !c.endDate) || null;
  const canAddContract = Boolean(employee && canManageEmployee(employee)
    && employee.openContractLimit?.canAddContract && primaryContract);
  const primaryEndYmd = primaryContract?.termEndDate ? dayjs.utc(primaryContract.termEndDate).format('YYYY-MM-DD') : null;
  // ADR-173 D6 — a secondary contract may not start before the primary
  const primaryStartYmd = primaryContract?.startDate ? dayjs.utc(primaryContract.startDate).format('YYYY-MM-DD') : null;

  const dialogs = (
    <>
      <AmendContractDialog
        open={Boolean(amendDialogEpisode)}
        onClose={() => setAmendDialogEpisode(null)}
        episode={amendDialogEpisode || {}}
        currentTermNames={{
          engagementModelId: amendedTerms.engagementModel?.name || '',
          contractCompanyId: amendedTerms.contractCompany?.name || '',
          contractTypeId: amendedTerms.contractType?.name || '',
          fullName: employee?.fullName || ''
        }}
        warnClusterId={employee?.cluster?.id || null}
        contracts={employee?.contracts || []}
        // null employee: each label reads only its own contract (no fallback
        // to the primary's number for a contract that has none)
        contractLabelOf={(contract) => contractIdentityLabel(null, contractAsEpisode(contract))}
        // ADR-188 D3 — the amended contract's own dates bound the effective date
        contract={amendedContract}
        onSubmitted={() => { setAmendDialogEpisode(null); load(); }}
      />
      <CancelAmendmentDialog
        open={Boolean(cancelDialogTarget)}
        onClose={() => setCancelDialogTarget(null)}
        episodeId={cancelDialogTarget?.episodeId}
        amendmentEvent={cancelDialogTarget?.event || null}
        onCancelled={() => { setCancelDialogTarget(null); load(); }}
      />
      <SuccessorPreviewDialog
        open={Boolean(successorPreviewTarget)}
        onClose={() => setSuccessorPreviewTarget(null)}
        episodeId={successorPreviewTarget?.episode?.id}
        eventId={successorPreviewTarget?.event?.id}
        contractLabel={successorPreviewTarget ? contractIdentityLabel(employee, successorPreviewTarget.episode) : null}
        canAct={Boolean(successorPreviewTarget?.canAct)}
      />
      <EditAmendmentDialog
        open={Boolean(editAmendmentTarget)}
        onClose={() => setEditAmendmentTarget(null)}
        episodeId={editAmendmentTarget?.episodeId}
        amendmentEvent={editAmendmentTarget?.event || null}
        warnClusterId={employee?.cluster?.id || null}
        onRevised={() => { setEditAmendmentTarget(null); load(); }}
      />
      {/* ADR-113 §5 — the shared lifecycle modals, mounted at their
          authoritative home. The episode scopes the "Already scheduled"
          panel and puts the contract's identity in the title. */}
      <AddContractDialog
        open={addContractOpen}
        onClose={() => setAddContractOpen(false)}
        employee={employee}
        primaryStartYmd={primaryStartYmd}
        primaryEndYmd={primaryEndYmd}
        onDone={async () => { setAddContractOpen(false); await load(); }}
      />
      <MakePrimaryDialog
        open={Boolean(primaryDialogEpisode)}
        onClose={() => setPrimaryDialogEpisode(null)}
        employeeId={employeeId}
        episode={primaryDialogEpisode}
        contractLabel={primaryDialogEpisode ? contractIdentityLabel(employee, primaryDialogEpisode) : null}
        onDone={async () => { setPrimaryDialogEpisode(null); await load(); }}
      />
      {/* ADR-159 §4.1 / ADR-149 §7 — "More actions ▾": the destructive
          contract verb, red, with an ellipsis because it opens a dialog. */}
      <Menu
        anchorEl={moreActions?.anchorEl || null}
        open={Boolean(moreActions?.anchorEl)}
        onClose={() => setMoreActions(null)}
      >
        {/* Ordinary first; the destructive Void Contract last, in red,
            below a divider. MUI Menu takes an array, not a Fragment. */}
        {[
          moreActions?.canSetNumber && (
            <MenuItem
              key="contract-number"
              onClick={() => {
                const target = moreActions?.episode || null;
                setMoreActions(null);
                setLifecycleTarget({ action: CONTRACT_ACTIONS.SET_CONTRACT_NUMBER, episode: target });
              }}
            >
              {moreActions?.hasNumber ? 'Edit contract number…' : 'Add contract number…'}
            </MenuItem>
          ),
          moreActions?.canMakePrimary && (
            <MenuItem
              key="make-primary"
              onClick={() => {
                const target = moreActions?.episode || null;
                setMoreActions(null);
                setPrimaryDialogEpisode(target);
              }}
            >
              Make Primary…
            </MenuItem>
          ),
          (moreActions?.canSetNumber || moreActions?.canMakePrimary) && moreActions?.canVoid
            && <Divider key="divider" />,
          moreActions?.canVoid && (
            <MenuItem
              key="void-contract"
              onClick={() => {
                const target = moreActions?.episode || null;
                setMoreActions(null);
                setLifecycleTarget({ action: CONTRACT_ACTIONS.VOID_CONTRACT, episode: target });
              }}
              sx={{ color: '#b91c1c' }}
            >
              Void Contract…
            </MenuItem>
          )
        ].filter(Boolean)}
      </Menu>
      <ContractActionModals
        action={lifecycleTarget?.action || null}
        employee={employee}
        episode={lifecycleTarget?.episode || null}
        onClose={() => setLifecycleTarget(null)}
        onCompleted={async () => { setLifecycleTarget(null); await load(); }}
      />
    </>
  );

  // 1..N card grid — one card per contract in force. A single contract
  // takes half the width on wide screens (a full-bleed card of six rows
  // reads badly); concurrent contracts (ADR-144) sit side by side.
  // §5.5 — what the count means: the contracts IN FORCE, or (for someone
  // with none) the single last contract, shown ended. ResultCount renders
  // the number itself, so this is the noun phrase only.

  const countLabel = openEpisodes.length > 0
    ? `active ${openEpisodes.length === 1 ? 'contract' : 'contracts'}`
    : 'last contract (ended)';

  return (
    <FormContainer>
      <FormSection>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <ResultCount>
            <span>{displayEpisodes.length}</span> {countLabel}
          </ResultCount>
          {/* ADR-171 C4 — offered only while the person's Region allows another
              open contract (ADR-167 D8) and they hold an open primary. */}
          {canAddContract && (
            <Button variant="outlined" sx={GHOST_SX} onClick={() => setAddContractOpen(true)}>
              Add Contract
            </Button>
          )}
        </Box>

        <Grid container spacing={3} alignItems="stretch">
          {displayEpisodes.map((episode) => (
            <Grid item xs={12} md={6} key={episode.id}>
              {renderContract(episode)}
            </Grid>
          ))}
        </Grid>

        <Typography sx={{ mt: 2, color: '#94a3b8', fontSize: 13 }}>
          Contract terms cannot be edited directly: they are set at hire or
          rehire and changed afterwards through the contract&apos;s own actions
          above — Amend Contract for the terms, the end-date actions for the
          contract end date. A contract&apos;s number is set or corrected from
          its More actions menu. Other data errors are corrected on the
          Employment Episodes tab.
        </Typography>

        {dialogs}
      </FormSection>
    </FormContainer>
  );
};

export default ContractDetails;
